import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Clock } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@sportscanner/ui";

interface RateLimitedEventDetail {
  retryAfter: number;
}

// Listens for `ss:rate-limited` events from the api helpers and shows a
// blocking dialog with a Retry-After countdown. Sits at the root of the app
// so it overlays whatever page the user is on. The dialog can't be dismissed
// until the countdown clears — closing it early would let the user re-trigger
// the limit immediately.
export default function RateLimitGate() {
  const { t } = useTranslation();
  const [retryAt, setRetryAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    function onRateLimited(e: Event) {
      const detail = (e as CustomEvent<RateLimitedEventDetail>).detail;
      const seconds = detail?.retryAfter ?? 60;
      const next = Date.now() + seconds * 1000;
      // Re-sample the clock here: `now` is seeded at mount and otherwise only
      // ticks via the interval (which starts after retryAt is set), so the
      // first render would compute remaining against a stale mount-time clock
      // and show an inflated countdown before snapping to the real value.
      setNow(Date.now());
      // Only extend the wait — never shrink it while a longer one is active.
      setRetryAt((prev) => (prev && prev > next ? prev : next));
    }
    window.addEventListener("ss:rate-limited", onRateLimited);
    return () => window.removeEventListener("ss:rate-limited", onRateLimited);
  }, []);

  useEffect(() => {
    if (retryAt === null) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [retryAt]);

  if (retryAt === null) return null;

  const remaining = Math.max(0, Math.ceil((retryAt - now) / 1000));
  const open = remaining > 0;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        showCloseButton={false}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="size-5" />
            {t("rateLimit.title")}
          </DialogTitle>
          <DialogDescription>{t("rateLimit.description")}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-center py-2">
          <div className="text-3xl font-semibold tabular-nums">
            {remaining}s
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
